<script>
import {mapGetters} from 'vuex';
import OutsideUSSupplies from '../assets/OutsideUSSupplies.vue';
import SingleUSSupplies from '../assets/SingleUSSupplies.vue';
import ManyUSSupplies from '../assets/ManyUSSupplies.vue';
import NoSupplies from '../assets/NoSupplies.vue';
import DeclarationItem from '../components/DeclarationItem.vue';

export default {
   name: 'SuppliesDeclarations',
   components: {DeclarationItem, NoSupplies, ManyUSSupplies, SingleUSSupplies, OutsideUSSupplies},
   data() {
      return {
         stateSelection: null,
      };
   },
   computed: {
      ...mapGetters({
         isSME: 'isSME',
         usStates: 'usStates',
         studyPeriodMap: 'companies/studyPeriodMap',
         supportedPeriodIds: 'supplies/supportedPeriodIds',
         completed: 'supplies/isCompleted',
         declaration: 'supplies/declaration',
         importedVendors: 'supplies/importedVendors',
         manualVendors: 'supplies/manualVendors',
         location: 'supplies/location',
      }),

      periodNames() {
         return this.supportedPeriodIds
            .map((id) => this.studyPeriodMap[id]?.label)
            .filter((label) => !!label);
      },

      prettyPeriodNames() {
         const formatter = new Intl.ListFormat('en', {
            style: 'long',
            type: 'disjunction',
         });
         return formatter.format(this.periodNames);
      },

      declaredState() {
         if (this.location) {
            return this.usStates[this.location];
         }
         return null;
      },

      stateOptions() {
         return Object.entries(this.usStates).map((entry) => {
            const [key, value] = entry;
            return {text: value, value: key};
         });
      },

      hasVendors() {
         return this.importedVendors.length > 0 || this.manualVendors.length > 0;
      },

      isDisabled() {
         return this.isSME || this.completed || this.hasVendors;
      },
   },
   methods: {
      async declareSupplies(value, location = null) {
         await this.$store.dispatch('supplies/saveDeclaration', {
            companyId: this.$companyId,
            value,
            location,
         });
      },

      openModalSelectState() {
         this.stateSelection = null;
         this.$bvModal.show('modal-select-state');
      },
   },
};
</script>

<template>
   <div>
      <h5 id="label-study-years" class="mb-3" style="color: black; font-size: 16px">
         Did any of these projects require any qualified supplies in the time period{{
            periodNames.length === 1 ? '' : 's'
         }}
         {{ prettyPeriodNames }}?
      </h5>
      <div class="declaration-container">
         <b-modal
            id="modal-select-state"
            title="Select a US State"
            centered
            @ok="declareSupplies('ONE', stateSelection)"
            :ok-disabled="stateSelection === null"
            ok-variant="success"
            style="color: black"
            cancel-variant="light"
            no-close-on-backdrop
            no-close-on-esc
         >
            <p>Select the US state in which all of your qualified R&D Supplies were consumed.</p>
            <b-form-select id="select-state" :options="stateOptions" v-model="stateSelection">
               <template #first>
                  <b-form-select-option :value="null" disabled>
                     Please select one
                  </b-form-select-option>
               </template>
            </b-form-select>
         </b-modal>
         <DeclarationItem
            :active="declaration === 'NONE'"
            :disabled="isDisabled"
            value="NONE"
            @input-declared="declareSupplies"
            expense-type="supplies"
         >
            <template v-slot:icon>
               <NoSupplies />
            </template>
            <template v-slot:description>
               <b>No, none</b> of these projects required supplies.
            </template>
         </DeclarationItem>
         <DeclarationItem
            :active="declaration === 'NOT_USA'"
            :disabled="isDisabled"
            value="NOT_USA"
            @input-declared="declareSupplies"
            expense-type="supplies"
         >
            <template v-slot:icon>
               <OutsideUSSupplies />
            </template>
            <template v-slot:description>
               <b>All</b> supplies were consumed <b>outside</b> the US.
            </template>
         </DeclarationItem>
         <DeclarationItem
            :active="declaration === 'ONE'"
            :disabled="isDisabled"
            value="ONE"
            @input-declared="openModalSelectState"
            expense-type="supplies"
         >
            <template v-slot:icon>
               <SingleUSSupplies />
            </template>
            <template v-slot:description>
               Supplies were <b>only</b> consumed in
               <b>{{ declaredState ? declaredState : 'one' }}</b
               >{{ declaredState ? '' : ' US State' }}.
            </template>
         </DeclarationItem>
         <DeclarationItem
            :active="declaration === 'MANY'"
            :disabled="isDisabled"
            value="MANY"
            @input-declared="declareSupplies"
            expense-type="supplies"
         >
            <template v-slot:icon>
               <ManyUSSupplies />
            </template>
            <template v-slot:description>
               Supplies were consumed in <b>more than one</b> US state.
            </template>
         </DeclarationItem>
      </div>
   </div>
</template>

<style scoped lang="scss">
.declaration-container {
   display: flex;
   flex-wrap: wrap;
   gap: 1rem;
   margin-bottom: 2rem;
}
</style>
